/**
 * =============================================================================
 * RFI STATUS-TRANSITION NOTICE
 * =============================================================================
 *
 * Maps a SURFACED RFI status-transition (`APPLIED` / `CLEARED` /
 * `CLEARED_AFTER_CANCEL` / `stale_lost` / `FAILED`, the only reasons the hook
 * ever exposes — Decision D-3) to the persistent inline notice the panel
 * renders. Because the modal's `questionStatus` is a stale
 * snapshot that never refreshes after a transition, the notice — not just the
 * transient toast — is the durable record of what happened. The `FAILED`
 * partial-success case is made explicit and unambiguous.
 *
 * =============================================================================
 */

import type {
  RfiCancelStatusTransition,
  RfiCompletionStatusTransition,
  RfiOpenStatusTransition,
} from '@find-my-part/types';
import { tokens } from '../../../tokens';

/** A status transition carrying a surfaced reason. */
export type SurfacedStatusTransition =
  | RfiOpenStatusTransition
  | RfiCompletionStatusTransition
  | RfiCancelStatusTransition;

/** Rendered notice descriptor. */
export interface StatusNotice {
  text: string;
  backgroundColor: string;
  borderColor: string;
}

/**
 * Resolve the inline notice for a surfaced transition. Returns `null` for any
 * non-surfaced reason (defensive — the hook only sets surfaced reasons).
 */
export function resolveStatusNotice(transition: SurfacedStatusTransition): StatusNotice | null {
  switch (transition.reason) {
    case 'APPLIED':
      return {
        text: 'Question moved to Needs More Info.',
        backgroundColor: tokens.color.status.warning.surface,
        borderColor: tokens.color.status.warning.accent,
      };
    case 'CLEARED':
      return {
        text: 'Question reopened — information received.',
        backgroundColor: tokens.color.status.success.surface,
        borderColor: tokens.color.status.success.accent,
      };
    case 'CLEARED_AFTER_CANCEL':
      return {
        // Cancel-correct copy — the info was NOT received; the request was withdrawn.
        text: 'Question reopened — request withdrawn.',
        backgroundColor: tokens.color.status.success.surface,
        borderColor: tokens.color.status.success.accent,
      };
    case 'stale_lost':
      return {
        text: 'Your RFI change was saved, but the question status changed elsewhere. Review the current question status before continuing.',
        backgroundColor: tokens.color.status.warning.surface,
        borderColor: tokens.color.status.warning.accent,
      };
    case 'FAILED':
      return {
        text: 'RFI saved, but the question status was not changed — change it manually if needed.',
        backgroundColor: tokens.color.status.warning.surface,
        borderColor: tokens.color.status.warning.accent,
      };
    default:
      return null;
  }
}
